import { AUTH_SESSION_URL } from '../models/constants';
import { clearAuthSession, persistAuthSession, readAuthSessionId } from '../utils/auth-storage';

export type DashboardAuthMode = 'dashboard' | 'none';

export interface ApiFetchOptions extends RequestInit {
  /** Adjuntar Bearer de sesión del panel (localStorage). Default: dashboard */
  auth?: DashboardAuthMode;
  /** Reintentar una vez tras restaurar sesión en 401. Default: true */
  retryOn401?: boolean;
  timeoutMs?: number;
}

let sessionExpiredHandler: (() => void) | null = null;
let restoreInFlight: Promise<string | null> | null = null;

export function setDashboardSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

function dashboardAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const sessionId = readAuthSessionId();
  return {
    Accept: 'application/json',
    ...(sessionId ? { Authorization: 'Bearer ' + sessionId } : {}),
    ...extra,
  };
}

async function restoreDashboardSessionOnce(sessionId: string): Promise<string | null> {
  const r = await fetch(AUTH_SESSION_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + sessionId,
    },
    body: JSON.stringify({ sessionId }),
  });
  const data = (await r.json().catch(() => ({}))) as {
    ok?: boolean;
    sessionId?: string;
    user?: { nombre?: string; username?: string };
    error?: string;
  };
  if (!r.ok || !data.ok) return null;
  const nextId = data.sessionId || sessionId;
  const userName = data.user?.nombre || data.user?.username || null;
  persistAuthSession(nextId, userName);
  return nextId;
}

async function restoreDashboardSession(): Promise<string | null> {
  if (restoreInFlight) return restoreInFlight;
  const sessionId = readAuthSessionId();
  if (!sessionId) return null;
  restoreInFlight = restoreDashboardSessionOnce(sessionId)
    .catch(() => null)
    .finally(() => {
      restoreInFlight = null;
    });
  return restoreInFlight;
}

export async function apiFetch(url: string, options: ApiFetchOptions = {}): Promise<Response> {
  const auth = options.auth ?? 'dashboard';
  const retryOn401 = options.retryOn401 !== false;
  const timeoutMs = options.timeoutMs ?? 35_000;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const baseHeaders =
    auth === 'dashboard'
      ? dashboardAuthHeaders((options.headers as Record<string, string>) ?? {})
      : { Accept: 'application/json', ...((options.headers as Record<string, string>) ?? {}) };

  try {
    let response = await fetch(url, {
      ...options,
      headers: baseHeaders,
      signal: options.signal ?? controller.signal,
    });

    if (response.status === 401 && auth === 'dashboard' && retryOn401) {
      const restored = await restoreDashboardSession();
      if (restored) {
        response = await apiFetch(url, {
          ...options,
          auth: 'dashboard',
          retryOn401: false,
          signal: options.signal ?? controller.signal,
        });
      } else {
        clearAuthSession();
        sessionExpiredHandler?.();
      }
    }

    return response;
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Tiempo de espera agotado. Intentá de nuevo.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export async function apiFetchJson<T>(
  url: string,
  options: ApiFetchOptions = {}
): Promise<{ ok: boolean; status: number; data: T }> {
  const response = await apiFetch(url, options);
  const data = (await response.json().catch(() => ({}))) as T;
  return { ok: response.ok, status: response.status, data };
}
